// SearchableCategorySelect.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { CategoryOption } from '../../lib/categories/categories';

interface SearchableCategorySelectProps {
  value: string;
  onChange: (value: string) => void;
  templateCategories?: CategoryOption[];
}

const SearchableCategorySelect = ({ 
  value, 
  onChange,
  templateCategories 
}: SearchableCategorySelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Use template categories if provided
  const options = templateCategories || [];

  // Filter categories based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (filteredOptions.length > 0) {
          const selectedOption = filteredOptions[highlightedIndex];
          onChange(selectedOption.value);
          setSearchTerm('');
          setIsOpen(false);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const handleOptionClick = (option: CategoryOption) => {
    onChange(option.value);
    setSearchTerm('');
    setIsOpen(false);
  };

  // Get display value from the template categories
  const displayValue = options.find(option => option.value === value)?.label || value;

  return (
    <div 
      ref={containerRef} 
      className="relative w-full"
    >
      <div
        className="flex items-center w-full border rounded px-2 py-1 cursor-pointer hover:bg-gray-50"
        onClick={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <div className="flex-grow text-gray-900 pr-2">
          {displayValue}
        </div>
        <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
          <input
            ref={searchInputRef}
            type="text"
            className="w-full px-2 py-1 border-b"
            placeholder="Type to search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-1 text-gray-500">
              No matching categories found
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                className={`px-2 py-1 cursor-pointer text-gray-900 ${
                  index === highlightedIndex ? 'bg-blue-100' : 'hover:bg-gray-100'
                }`}
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableCategorySelect;
